import { Skeleton } from "@/components/ui/skeleton";
import { isOverviewStatSelected, selectOverviewStat } from "@/lib/invoiceOverviewStats";
import { getInsightCount } from "@/lib/overviewInsights";

function OverviewStat({ label, value, display, color, share, selected, onSelect }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className="overview-stat"
      data-hot={value > 0 ? "true" : "false"}
      style={{ "--stat-color": color, "--stat-share": `${Math.round(share * 100)}%` }}
    >
      <span className="overview-figure">{display}</span>
      <span className="overview-stat-label">{label}</span>
      <span className="overview-meter" aria-hidden="true">
        <span />
      </span>
    </button>
  );
}

/**
 * Counts from the insights endpoint — not a tally of the rows on screen. The
 * endpoint is asked with the list's own filters, and each tile's count ignores
 * its own filter, so a tile's number is the number of rows its click lists.
 * `stats` is the set of tiles the queue shows.
 */
export function InvoiceOverview({
  stats,
  insights,
  isLoading = false,
  selectedChip = "all",
  onSelectChip,
  selectedCategory = "",
  onSelectCategory,
  selectedFailureType = "",
  onSelectFailureType,
}) {
  if (isLoading && !insights) {
    return (
      <div className="overview-strip" aria-label="Invoice overview">
        <div className="overview-strip-track">
          {stats.map((stat) => (
            <Skeleton key={stat.key} className="h-16 flex-1 rounded-[0.8rem]" />
          ))}
        </div>
      </div>
    );
  }

  // A count the endpoint did not return reads as a dash, not as a real 0.
  const counts = stats.map((stat) => getInsightCount(insights, stat.source));
  const values = counts.map((count) => Number(count ?? 0));
  const displays = counts.map((count) => count ?? "–");
  const peak = Math.max(1, ...values);
  const dimensions = {
    queueChip: selectedChip,
    categoryFilter: selectedCategory,
    failureTypeFilter: selectedFailureType,
    onSelectChip,
    onSelectCategory,
    onSelectFailureType,
  };

  return (
    <div className="overview-strip" aria-label="Invoice overview">
      <div className="overview-strip-track">
        {stats.map((stat, index) => (
          <OverviewStat
            key={stat.key}
            label={stat.label}
            value={values[index]}
            display={displays[index]}
            color={stat.color}
            share={values[index] / peak}
            selected={isOverviewStatSelected(stat, dimensions)}
            onSelect={() => selectOverviewStat(stat, dimensions)}
          />
        ))}
      </div>
    </div>
  );
}
